/**
 * useProfile Hook
 * Provides current user profile data and update mutation
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "convex/_generated/api";
import { useApiMutation } from "./use-api-mutation";

export interface UseProfileReturn {
  user: {
    name: string;
    email: string;
    jobTitle?: string;
    timezone?: string;
    avatarColor: string;
    imageUrl?: string;
    deletionScheduledAt?: number;
  } | null;
  isLoading: boolean;
  updateProfile: ReturnType<typeof useApiMutation>;
}

const AVATAR_COLORS = [
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#EC4899",
  "#06B6D4",
  "#F97316",
];

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return Math.abs(hash);
}

function getAvatarColor(name: string): string {
  return AVATAR_COLORS[hashString(name) % AVATAR_COLORS.length];
}

export function useProfile() {
  const { data: currentUser, isLoading } = useQuery(
    convexQuery(api.users.getCurrent, {}),
  );

  const updateProfileMutation = useApiMutation(api.users.updateProfile, {
    name: "update-profile",
    successMessage: "Profile updated",
  });

  const userName = currentUser?.name ?? currentUser?.email ?? "User";

  const user = useMemo(() => {
    if (!currentUser) return null;
    return {
      name: userName,
      email: currentUser.email ?? "",
      jobTitle: currentUser.jobTitle ?? undefined,
      timezone: currentUser.timezone ?? undefined,
      avatarColor: getAvatarColor(userName),
      imageUrl: (currentUser as Record<string, unknown>).image as
        | string
        | undefined,
      deletionScheduledAt: currentUser.deletionScheduledAt ?? undefined,
    };
  }, [currentUser, userName]);

  return {
    user,
    isLoading,
    updateProfile: updateProfileMutation,
  };
}
